<script setup>
import CtaBanner from '@/views/front-pages/landing-page/CtaBanner.vue'
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

// Addendum 15, Modul BO: data testimoni SUDAH ADA sejak Addendum 14 (Modul
// BM) -- endpoint publik GET /api/public/testimonials (hanya status
// approved, difilter di backend, Public\TestimonialController). Halaman ini
// murni menambah alamat URL tersendiri (sebelumnya cuma tampil sebagai satu
// section pendek di Beranda, TestimonialsSection.vue -- yang tetap ada di
// sana, tidak dihapus).
const testimonials = ref([])
const loading = ref(true)

const fetchTestimonials = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/public/testimonials').get()
    const body = response?.data ?? response

    testimonials.value = Array.isArray(body) ? body : (body?.data ?? [])
  } catch (err) {
    console.error('❌ Gagal memuat testimoni:', err)
    testimonials.value = []
  } finally {
    loading.value = false
  }
}

onMounted(fetchTestimonials)

const AVATAR_COLORS = ['primary', 'success', 'warning', 'info', 'error', 'secondary']

const avatarColorFor = name => {
  const code = [...(name || '?')].reduce((sum, ch) => sum + ch.charCodeAt(0), 0)

  return AVATAR_COLORS[code % AVATAR_COLORS.length]
}

const initialsFor = name => {
  if (!name) return '?'

  return name.trim().split(/\s+/).slice(0, 2).map(word => word[0]?.toUpperCase()).join('')
}
</script>

<template>
  <PublicPageShell>
    <PageHero
      eyebrow="Testimoni"
      eyebrow-icon="ri-chat-quote-line"
      eyebrow-color="error"
      title="Apa Kata Pelanggan Kami"
      subtitle="Testimoni langsung dari pemilik bisnis yang sudah memakai PintuKasir sehari-hari."
    />

    <section class="testimonials-page-section pb-16">
      <VContainer>
        <div
          v-if="loading"
          class="text-center py-12"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-else-if="testimonials.length === 0"
          class="text-center py-12"
        >
          <VIcon
            icon="ri-chat-quote-line"
            size="48"
            class="text-medium-emphasis mb-4"
          />
          <h2 class="text-h6 font-weight-bold mb-2">
            Belum Ada Testimoni Tampil
          </h2>
          <p
            class="text-medium-emphasis mx-auto"
            style="max-width: 480px"
          >
            Testimoni yang sudah disetujui tim kami akan muncul di sini. Sudah jadi pelanggan PintuKasir? Kirim testimoni Anda lewat menu "Beri Testimoni" di dashboard.
          </p>
        </div>

        <VRow v-else>
          <VCol
            v-for="(testimonial, index) in testimonials"
            :key="`${testimonial.business_name}-${index}`"
            cols="12"
            md="4"
          >
            <VCard
              class="testimonial-card h-100"
              rounded="xl"
              variant="outlined"
            >
              <VCardText class="pa-6 d-flex flex-column">
                <div class="d-flex align-center gap-1 mb-4">
                  <VIcon
                    v-for="i in testimonial.rating"
                    :key="i"
                    icon="ri-star-fill"
                    color="warning"
                    size="16"
                  />
                </div>

                <blockquote class="testimonial-quote flex-grow-1 mb-5">
                  "{{ testimonial.message }}"
                </blockquote>

                <VDivider class="mb-4" />

                <div class="d-flex align-center gap-3">
                  <VAvatar
                    :color="avatarColorFor(testimonial.business_name)"
                    size="44"
                    class="text-white font-weight-bold"
                  >
                    {{ initialsFor(testimonial.business_name) }}
                  </VAvatar>
                  <div
                    class="font-weight-bold text-high-emphasis"
                    style="font-size: 0.9375rem"
                  >
                    {{ testimonial.business_name }}
                  </div>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>

    <CtaBanner />
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.testimonials-page-section {
  background: rgba(var(--v-theme-surface), 1);
}

.testimonial-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(var(--v-shadow-key-umbra-color), 10%) !important;
    transform: translateY(-2px);
  }
}

.testimonial-quote {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-style: italic;
  margin: 0;
  padding: 0;
}
</style>
